import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User, Mail, Phone, Calendar, Car, FileBadge, MapPin,
  CheckCircle2, ArrowRight, Truck
} from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { PageHeader, SectionHeading } from "../components/sections/Shared";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const PERKS = [
  { icon: "$", title: "$22–$32 / hour", text: "Top earners hit $1,200+ per week." },
  { icon: "⏱", title: "Flexible shifts", text: "Pick mornings, evenings, weekends — or all three." },
  { icon: "🏦", title: "Local routes only", text: "Home every night. No long-haul, ever." },
  { icon: "✨", title: "Same-week pay", text: "Direct deposit every Friday." }
];

export default function DriverSignup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", city: "",
    licenseClass: "", yearsDriving: "", vehicleType: "", availability: "",
    about: "", agree: false
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) {
      toast({ title: "Please accept the terms", description: "You must agree to the conditions." });
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "driver_applications"), {
        ...form,
        status: "pending",
        createdAt: serverTimestamp()
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast({
        title: "Couldn't submit application",
        description: err?.message || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Drive with 518 Courier — Join Our Fleet in Albany, NY"
        description="Apply to drive with 518 Courier. Local courier and delivery routes across the Capital Region. Flexible hours, same-week pay. Apply online today."
        path="/driver-signup"
      />
      <Navbar />
      <main>
        <PageHeader
          eyebrow="Drive with us"
          title="Join the"
          accent="518 Courier fleet."
          description="We're hiring full and part-time drivers across the Capital Region. Local routes only, same-week pay, and real human dispatch."
          image="https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjb3VyaWVyJTIwdmFufGVufDB8fHx8MTc4MDI1MzQwOHww&ixlib=rb-4.1.0&q=85"
        />

        {/* PERKS */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-518 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map((p, i) => (
              <div key={i} className="card-lift bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-[#f59321] text-white font-display font-extrabold text-2xl flex items-center justify-center mb-4">
                  {p.icon}
                </div>
                <h4 className="font-display font-bold text-[#0f2847] text-lg mb-1">{p.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* APPLICATION */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="container-518 max-w-3xl">
            <SectionHeading
              eyebrow="Apply now"
              title="Tell us about"
              accent="yourself."
              description="Takes about 3 minutes. We'll review and reach out within 48 hours."
            />

            {submitted ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847]">
                  Application received.
                </h3>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">
                  Thanks {form.firstName || ""}! Our hiring team will review your application and reach out within 48 hours.
                </p>
                <div className="mt-7 flex flex-wrap gap-3 justify-center">
                  <Button onClick={() => navigate("/")} className="bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-12 px-6 font-semibold">
                    Back to home
                  </Button>
                  <Button
                    onClick={() => navigate("/driver-login")}
                    variant="outline"
                    className="rounded-full h-12 px-6 font-semibold"
                  >
                    Existing driver? Sign in
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-9 shadow-sm space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field icon={User} label="First name" required value={form.firstName} onChange={(v) => update("firstName", v)} />
                  <Field label="Last name" required value={form.lastName} onChange={(v) => update("lastName", v)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field icon={Mail} type="email" label="Email" required value={form.email} onChange={(v) => update("email", v)} />
                  <Field icon={Phone} type="tel" label="Phone" required value={form.phone} onChange={(v) => update("phone", v)} />
                </div>
                <Field icon={MapPin} label="City you live in" required value={form.city} onChange={(v) => update("city", v)} placeholder="Albany, NY" />

                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField
                    icon={FileBadge}
                    label="Driver's license class"
                    value={form.licenseClass}
                    onChange={(v) => update("licenseClass", v)}
                    options={["Class D (regular)", "Class C", "CDL Class A", "CDL Class B"]}
                  />
                  <SelectField
                    icon={Calendar}
                    label="Years driving"
                    value={form.yearsDriving}
                    onChange={(v) => update("yearsDriving", v)}
                    options={["1–2 years", "3–5 years", "6–10 years", "10+ years"]}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField
                    icon={Car}
                    label="Vehicle you'd drive"
                    value={form.vehicleType}
                    onChange={(v) => update("vehicleType", v)}
                    options={["Own car (sedan/SUV)", "Own cargo van", "518 fleet vehicle"]}
                  />
                  <SelectField
                    icon={Truck}
                    label="Availability"
                    value={form.availability}
                    onChange={(v) => update("availability", v)}
                    options={["Full-time (40+ hrs)", "Part-time (20–39 hrs)", "Weekends only", "Evenings only", "Flexible / on-call"]}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#0f2847] font-semibold">A bit about you (optional)</Label>
                  <Textarea
                    rows={3}
                    value={form.about}
                    onChange={(e) => update("about", e.target.value)}
                    placeholder="Tell us about your driving experience or anything you'd like us to know."
                    className="rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321] resize-none"
                  />
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                  <Checkbox
                    checked={form.agree}
                    onCheckedChange={(v) => update("agree", v)}
                    className="mt-0.5 data-[state=checked]:bg-[#f59321] data-[state=checked]:border-[#f59321]"
                  />
                  <span>
                    I authorize 518 Courier to perform a background check and verify my information per the{" "}
                    <Link to="/conditions" className="text-[#f59321] font-semibold hover:underline">Conditions</Link>.
                  </span>
                </label>
                <Button type="submit" disabled={submitting} className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-13 h-14 shadow-md disabled:opacity-60">
                  {submitting ? "Submitting…" : "Submit application"}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Field({ icon: Icon, label, required, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0f2847] font-semibold">{label}{required && " *"}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <Input
          required={required}
          {...props}
          onChange={(e) => props.onChange(e.target.value)}
          className={`h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321] ${Icon ? "pl-10" : ""}`}
        />
      </div>
    </div>
  );
}

function SelectField({ icon: Icon, label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0f2847] font-semibold">{label} *</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:ring-[#f59321]">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-slate-400" />}
            <SelectValue placeholder="Select..." />
          </div>
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
